import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';

const BodyFatCalculator = () => {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(28);
  const [weight, setWeight] = useState(70);
  const [neck, setNeck] = useState(38);
  const [waist, setWaist] = useState(85);
  const [hip, setHip] = useState(95);
  const [height, setHeight] = useState(170);

  const { fatPct, fatMass, leanMass, category } = useMemo(() => {
    // US Navy method (cm)
    let pct;
    if (gender === 'male') {
      pct = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
    } else {
      pct = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height)) - 450;
    }
    pct = Math.max(0, Math.min(pct, 60));
    const fm = (weight * pct) / 100;
    let cat = '';
    if (gender === 'male') {
      if (pct < 6) cat = 'Essential Fat';
      else if (pct < 14) cat = 'Athletes';
      else if (pct < 18) cat = 'Fitness';
      else if (pct < 25) cat = 'Average';
      else cat = 'Obese';
    } else {
      if (pct < 14) cat = 'Essential Fat';
      else if (pct < 21) cat = 'Athletes';
      else if (pct < 25) cat = 'Fitness';
      else if (pct < 32) cat = 'Average';
      else cat = 'Obese';
    }
    return { fatPct: pct, fatMass: fm, leanMass: weight - fm, category: cat };
  }, [gender, weight, neck, waist, hip, height]);

  return (
    <CalculatorLayout
      title="Body Fat Calculator"
      subtitle="Estimate body fat percentage using the U.S. Navy method."
      category="Fitness"
      description={<>
        <p>This calculator uses the U.S. Navy circumference method, which estimates body fat from neck, waist, and (for women) hip measurements along with height.</p>
        <p>Measure with a flexible tape, snug but not compressing skin, while standing relaxed. Results are estimates; DEXA scans are more accurate.</p>
      </>}
      formulaNote="Men: 495 / (1.0324 − 0.19077×log₁₀(waist−neck) + 0.15456×log₁₀(height)) − 450"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Gender</label>
            <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="male" /> Male</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="female" /> Female</label>
            </RadioGroup>
          </div>
          <SliderInput label="Age" unit="yr" value={age} onChange={setAge} min={15} max={100} step={1} />
          <SliderInput label="Weight" unit="kg" value={weight} onChange={setWeight} min={30} max={200} step={0.5} />
          <SliderInput label="Height" unit="cm" value={height} onChange={setHeight} min={120} max={220} step={1} />
          <SliderInput label="Neck" unit="cm" value={neck} onChange={setNeck} min={20} max={60} step={0.5} />
          <SliderInput label="Waist" unit="cm" value={waist} onChange={setWaist} min={50} max={150} step={0.5} />
          {gender === 'female' && (
            <SliderInput label="Hip" unit="cm" value={hip} onChange={setHip} min={60} max={160} step={0.5} />
          )}
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Body Fat</div>
          <div className="font-display text-5xl font-bold text-bharat-navy display-font mt-1">{fatPct.toFixed(1)}%</div>
          <div className="inline-block mt-2 px-3 py-1 rounded-full bg-saffron-500 text-white text-sm font-semibold">{category}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="bg-white rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Fat Mass</div>
              <div className="display-font font-semibold mt-0.5 text-saffron-700">{fatMass.toFixed(1)} kg</div>
            </div>
            <div className="bg-white rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Lean Mass</div>
              <div className="display-font font-semibold mt-0.5 text-indgreen-700">{leanMass.toFixed(1)} kg</div>
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default BodyFatCalculator;
